import { useState } from "react";
import { FileText, Image as ImageIcon, MoreVertical, Trash2 } from "lucide-react";
import { previewUrl } from "@/lib/filePreview";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import FilePreviewDialog from "@/components/order/FilePreviewDialog";

function formatSize(bytes = 0) {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileKind(file) {
  const type = file?.type || "";
  const name = file?.name || "";
  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf" || name.toLowerCase().endsWith(".pdf")) return "pdf";
  return "file";
}

function configSummary(opts = {}) {
  const parts = [];

  if (opts.copies) parts.push(`${opts.copies} copia${Number(opts.copies) === 1 ? "" : "s"}`);
  if (opts.color === "bw") parts.push("B/N");
  if (opts.color === "color") parts.push("Color");
  if (opts.sides === "single") parts.push("Una cara");
  if (opts.sides === "double") parts.push("Doble cara");
  if (opts.size) parts.push(opts.size);

  const paperLabels = {
    normal: "Papel normal",
    cartulina: "Cartulina",
    fotografico: "Fotográfico",
    pegatina: "Pegatina",
    "papel-80g": "Papel 80 g",
    "papel-100g": "Papel 100 g",
    "papel-120g": "Papel 120 g",
    "papel-brillo-150g": "Brillo 150 g",
    "cartulina-brillo-350g": "Brillo 350 g",
  };

  if (opts.paper) parts.push(paperLabels[opts.paper] || opts.paper);

  if (Array.isArray(opts.finishes) && opts.finishes.length > 0) {
    parts.push(`${opts.finishes.length} acabado${opts.finishes.length === 1 ? "" : "s"}`);
  }

  return parts;
}

export default function FileCard({ entry, index, opts, onDelete }) {
  const file = entry.file;
  const kind = fileKind(file);
  const pages = entry.pages;
  const summary = configSummary(opts);
  const imagePreview = previewUrl(file);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  return (
    <li
      data-testid={`file-card-${index}`}
      className="overflow-hidden rounded-3xl border-2 border-slate-200 bg-white shadow-sm"
    >
      <div className="flex gap-3 p-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`${expanded ? "h-36 w-28" : "h-24 w-24"} flex shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 transition-all`}
          title={expanded ? "Contraer archivo" : "Ampliar archivo"}
        >
          {imagePreview ? (
            <img
              src={imagePreview}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : kind === "pdf" ? (
            <FileText className="h-10 w-10 text-[#4d7c0f]" />
          ) : (
            <ImageIcon className="h-10 w-10 text-slate-400" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-black text-slate-900">
            {file.name}
          </div>

          <div className="mt-1 text-xs font-semibold text-slate-500">
            {formatSize(file.size)}
            {pages === undefined && " · contando páginas…"}
            {typeof pages === "number" && ` · ${pages} página${pages === 1 ? "" : "s"}`}
            {pages === null && " · páginas no detectadas"}
          </div>

          <div className="mt-3 rounded-xl bg-[#4d7c0f]/5 px-3 py-2 text-xs font-bold text-[#365314]">
            La configuración del pedido se aplicará a este archivo.
            {summary.length > 0 && (
              <div className="mt-1 text-slate-700">
                {summary.join(" · ")}
              </div>
            )}
          </div>
        </div>

        <div className="relative self-start">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid={`file-menu-${index}`}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title="Opciones del archivo"
          >
            <MoreVertical className="h-4 w-4" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 z-10 mt-1 w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setPreviewOpen(true);
                }}
                data-testid={`preview-file-${index}`}
                className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-black text-slate-700 hover:bg-slate-50"
              >
                <FileText className="h-4 w-4" />
                Ver archivo
              </button>

              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setConfirmOpen(true);
                }}
                data-testid={`delete-file-${index}`}
                className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-black text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Eliminar archivo del pedido
              </button>
            </div>
          )}
        </div>
      </div>


      <FilePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        file={file}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Eliminar archivo del pedido"
        description={`¿Seguro que quieres eliminar "${file.name}" de este pedido?\n\nEl archivo dejará de imprimirse.`}
        confirmText="Eliminar archivo"
        danger
        onConfirm={() => onDelete?.(index)}
      />
    </li>
  );
}
